/**
 * Auth Diagnostic Utility
 *
 * Use in browser console:
 *   import('/src/utils/authDiagnostic.ts').then(m => m.diagnoseAuth())
 *
 * Or call from dev tools:
 *   window.__diagnoseAuth?.()
 */

interface DiagnosticResult {
  timestamp: string;
  environment: {
    url: string;
    pathname: string;
  };
  auth: {
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
    hasUserData: boolean;
  };
  user: {
    id: string | null;
    email: string | null;
    name: string | null;
    role: string | null;
    permissions: string[];
  } | null;
  issues: string[];
  recommendations: string[];
}

export function diagnoseAuth(): DiagnosticResult {
  const issues: string[] = [];
  const recommendations: string[] = [];

  // Get stored data
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  const userDataStr = localStorage.getItem('user');

  let userData = null;
  try {
    userData = userDataStr ? JSON.parse(userDataStr) : null;
  } catch {
    issues.push('User data in localStorage is corrupted (invalid JSON)');
    recommendations.push('Clear localStorage and re-login: localStorage.clear(); location.reload();');
  }

  // Check for missing tokens
  if (!accessToken) {
    issues.push('No access token found - user is not authenticated');
    recommendations.push('Navigate to /login to authenticate');
  }

  if (!refreshToken) {
    issues.push('No refresh token found - session cannot be refreshed');
  }

  if (!userData) {
    issues.push('No user data found in localStorage');
    if (accessToken) {
      recommendations.push('Token exists but user data missing - try re-logging in');
    }
  }

  // Validate user role
  if (userData) {
    const validRoles = ['CUSTOMER', 'THERAPIST', 'ADMIN', 'SUPER_ADMIN'];
    if (!validRoles.includes(userData.role)) {
      issues.push(`Invalid role detected: "${userData.role}"`);
      recommendations.push('Role should be one of: ' + validRoles.join(', '));
    }

    // Check for expected dashboard based on role
    const expectedDashboard: Record<string, string> = {
      CUSTOMER: '/dashboard',
      THERAPIST: '/therapist/dashboard',
      ADMIN: '/admin',
      SUPER_ADMIN: '/admin',
    };

    const expected = expectedDashboard[userData.role];
    const current = window.location.pathname;

    if (expected && current.startsWith('/admin') && userData.role === 'CUSTOMER') {
      issues.push('Customer trying to access admin dashboard');
      recommendations.push(`Navigate to ${expected} for your role`);
    }

    if (expected && current.startsWith('/admin') && userData.role === 'THERAPIST') {
      issues.push('Therapist trying to access admin dashboard');
      recommendations.push(`Navigate to ${expected} for your role`);
    }

    // Check permissions array
    if (!userData.permissions || !Array.isArray(userData.permissions)) {
      issues.push('Permissions array missing or invalid');
      recommendations.push('Re-login to get fresh permissions from server');
    } else if (userData.permissions.length === 0 && (userData.role === 'ADMIN' || userData.role === 'SUPER_ADMIN')) {
      issues.push('Admin user has no permissions - using fallback matrix');
      recommendations.push('Re-login to get server-provided permissions');
    }
  }

  // Token expiry check (basic - assumes JWT)
  if (accessToken) {
    try {
      const [, payload] = accessToken.split('.');
      if (payload) {
        const decoded = JSON.parse(atob(payload));
        if (decoded.exp) {
          const expiry = new Date(decoded.exp * 1000);
          const now = new Date();
          if (expiry < now) {
            issues.push(`Access token expired at ${expiry.toISOString()}`);
            recommendations.push('Token expired - re-login required');
          } else {
            const hoursLeft = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60);
            if (hoursLeft < 1) {
              issues.push(`Access token expires soon (${hoursLeft.toFixed(2)} hours left)`);
            }
          }
        }
      }
    } catch {
      // Token might not be standard JWT, skip expiry check
    }
  }

  const result: DiagnosticResult = {
    timestamp: new Date().toISOString(),
    environment: {
      url: window.location.href,
      pathname: window.location.pathname,
    },
    auth: {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      hasUserData: !!userData,
    },
    user: userData
      ? {
          id: userData.id || null,
          email: userData.email || null,
          name: userData.name || null,
          role: userData.role || null,
          permissions: userData.permissions || [],
        }
      : null,
    issues,
    recommendations,
  };

  // Console output with styling
  console.group('%c🔍 Auth Diagnostic Report', 'font-size: 16px; font-weight: bold; color: #3b82f6;');

  console.log('%cTimestamp:', 'font-weight: bold;', result.timestamp);
  console.log('%cCurrent URL:', 'font-weight: bold;', result.environment.url);

  console.group('%c📋 Auth State', 'font-weight: bold; color: #10b981;');
  console.table(result.auth);
  console.groupEnd();

  if (result.user) {
    console.group('%c👤 User Data', 'font-weight: bold; color: #8b5cf6;');
    console.log('ID:', result.user.id);
    console.log('Email:', result.user.email);
    console.log('Name:', result.user.name);
    console.log('%cRole: %c' + result.user.role, 'font-weight: bold;', 'color: #f59e0b; font-weight: bold;');
    console.log('Permissions:', result.user.permissions);
    console.groupEnd();
  } else {
    console.log('%c👤 User: Not logged in', 'color: #ef4444;');
  }

  if (result.issues.length > 0) {
    console.group('%c⚠️ Issues Found', 'font-weight: bold; color: #ef4444;');
    result.issues.forEach((issue, i) => console.log(`${i + 1}. ${issue}`));
    console.groupEnd();
  } else {
    console.log('%c✅ No issues detected', 'color: #10b981; font-weight: bold;');
  }

  if (result.recommendations.length > 0) {
    console.group('%c💡 Recommendations', 'font-weight: bold; color: #f59e0b;');
    result.recommendations.forEach((rec, i) => console.log(`${i + 1}. ${rec}`));
    console.groupEnd();
  }

  console.groupEnd();

  return result;
}

// Helper function to clear auth and reload
export function clearAuthAndReload(): void {
  console.log('Clearing auth data...');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  console.log('Auth data cleared. Reloading...');
  window.location.reload();
}

// Attach diagnostic tools to window for console access
// These are read-only diagnostics and safe logout - no security risk
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__diagnoseAuth = diagnoseAuth;
  (window as unknown as Record<string, unknown>).__clearAuthAndReload = clearAuthAndReload;
}

export default diagnoseAuth;
